import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, Loader2 } from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EmailCapture() {
  const [email, setEmail] = useState('');
  const [hp, setHp] = useState(''); // honeypot — bots fill, humans don't
  const [state, setState] = useState('idle'); // idle | submitting | error | success
  const [errorMsg, setErrorMsg] = useState('Please enter a valid email.');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setErrorMsg('Please enter a valid email.');
      setState('error');
      return;
    }
    setState('submitting');
    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), website: hp }),
      });
      if (!res.ok) {
        setErrorMsg("Couldn't add you just now. Try again?");
        setState('error');
        return;
      }
      setState('success');
    } catch {
      setErrorMsg('Network issue. Try again?');
      setState('error');
    }
  }

  const isSuccess = state === 'success';
  const isSubmitting = state === 'submitting';

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-md">
      <div
        className={`group flex items-center gap-1 rounded-full border bg-pure-surface p-1.5 shadow-card transition-all duration-300 ${
          state === 'error'
            ? 'border-red-400'
            : 'border-soft-border focus-within:border-ink focus-within:shadow-elevated'
        }`}
      >
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (state === 'error') setState('idle');
          }}
          placeholder="you@example.com"
          disabled={isSuccess || isSubmitting}
          aria-label="Email address"
          className="flex-1 bg-transparent px-4 py-2 text-sm text-ink placeholder:text-slate-text focus:outline-none disabled:opacity-60"
        />
        {/* Honeypot — visually hidden but reachable by simple bots */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={hp}
          onChange={(e) => setHp(e.target.value)}
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-9999px',
            width: '1px',
            height: '1px',
            opacity: 0,
            pointerEvents: 'none',
          }}
        />
        <button
          type="submit"
          disabled={isSuccess || isSubmitting}
          className="relative inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm font-medium text-pure-surface transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:bg-saffron-gold disabled:text-ink disabled:hover:scale-100"
        >
          <AnimatePresence mode="wait" initial={false}>
            {isSuccess ? (
              <motion.span
                key="done"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="inline-flex items-center gap-1.5"
              >
                <Check className="h-4 w-4" /> You're on the list
              </motion.span>
            ) : isSubmitting ? (
              <motion.span
                key="loading"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="inline-flex items-center gap-1.5"
              >
                <Loader2 className="h-4 w-4 animate-spin" /> Adding…
              </motion.span>
            ) : (
              <motion.span
                key="cta"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="inline-flex items-center gap-1.5"
              >
                Notify me <ArrowRight className="h-4 w-4" />
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>

      <div className="h-5 px-2 pt-1.5 text-xs">
        <AnimatePresence mode="wait">
          {state === 'error' && (
            <motion.p
              key="err"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="text-red-500"
            >
              {errorMsg}
            </motion.p>
          )}
          {state === 'success' && (
            <motion.p
              key="ok"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="text-slate-text"
            >
              We'll write only when something matters.
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </form>
  );
}
